import type { DeparturesByDate } from './cruiseDepartures'
import {
  getCabinCategories,
  getCruiseOfferMemberPriceVariation,
  getCruiseOfferPriceVariation,
  type CruiseOfferPriceVariation,
} from './cruiseUtils'

export type CruiseDeparturePriceTotals = Record<string, Record<string, { total: number }>>

interface DeparturesWithBundlePrices {
  displayedPrices: CruiseDeparturePriceTotals
  displayedDepartures: DeparturesByDate
}

export interface CruiseDepartureCabinPrice {
  category: string
  isBundle?: boolean
  discountPercentage?: number
  hasPrice: boolean
  memberPrice?: number
  memberPriceType?: App.Cruises.CruiseMemberPriceType
  primaryPrice: CruiseOfferPriceVariation['primaryPrice']
  secondaryPrice?: CruiseOfferPriceVariation['secondaryPrice']
  promotionalPrice?: CruiseOfferPriceVariation['promotionalPrice']
}

export function getCruiseDeparturePrices(
  lowestDeparturePricesByCategory: CruiseDeparturePriceTotals,
  departureId: string,
): Array<number> {
  return Object.values(lowestDeparturePricesByCategory[departureId] ?? {})
    .map((price) => price.total)
    .filter((price) => Number.isFinite(price) && price > 0)
}

export function isCruiseDepartureSoldOut(
  lowestDeparturePricesByCategory: CruiseDeparturePriceTotals,
  departureId: string,
): boolean {
  return getCruiseDeparturePrices(lowestDeparturePricesByCategory, departureId).length === 0
}

export function getLowestBundleDeparturePrice(
  departuresByDate: DeparturesByDate,
  bundlePricesByDeparture: App.LowestCabinDetailsByDepartureId | undefined,
): number | undefined {
  if (!bundlePricesByDeparture) return undefined

  let lowestPrice: number | undefined
  for (const departures of Object.values(departuresByDate)) {
    for (const departure of departures) {
      for (const price of getCruiseDeparturePrices(bundlePricesByDeparture, departure.id)) {
        lowestPrice = lowestPrice === undefined ? price : Math.min(lowestPrice, price)
      }
    }
  }

  return lowestPrice
}

export function getDeparturesWithBundlePrices(
  departuresByDate: DeparturesByDate,
  standardPricesByDeparture: App.LowestDeparturePricesByCategory,
  bundlePricesByDeparture: App.LowestCabinDetailsByDepartureId | undefined,
): DeparturesWithBundlePrices {
  const displayedPrices: CruiseDeparturePriceTotals = {}
  const displayedDepartures: DeparturesByDate = {}

  for (const [date, departures] of Object.entries(departuresByDate)) {
    displayedDepartures[date] = []

    for (const departure of departures) {
      const bundlePrices: App.LowestPriceDetailsByCategory = {}
      for (const [category, price] of Object.entries(bundlePricesByDeparture?.[departure.id] ?? {})) {
        if (Number.isFinite(price.total) && price.total > 0) {
          bundlePrices[category] = price
        }
      }

      if (Object.keys(bundlePrices).length === 0) continue

      displayedPrices[departure.id] = {
        ...standardPricesByDeparture[departure.id],
        ...bundlePrices,
      }
      displayedDepartures[date].push(departure)
    }
  }

  return { displayedPrices, displayedDepartures }
}

export function getCruiseDepartureCabinPrices(
  offer: App.CruiseOffer,
  departure: App.CruiseDepartureDetails,
  departureMemberPricingByCategory: Record<string, App.Cruises.CruiseOfferMemberPriceByCategory>,
  includeHotel = false,
): Array<CruiseDepartureCabinPrice> {
  const pricesByCabinCategory = offer.lowestDeparturePricesByCategory[departure.id]

  const bundlePrices = includeHotel ? offer.lowestBundleDeparturePricesByCategory?.[departure.id] : undefined
  if (isCruiseDepartureSoldOut(offer.lowestDeparturePricesByCategory, departure.id) &&
    !Object.values(bundlePrices ?? {}).some((price) => Number.isFinite(price.total) && price.total > 0)) return []

  return getCabinCategories(offer.cruiseType).map((item) => {
    const category = item.category.toLowerCase()
    const bundlePrice = bundlePrices?.[category]
    const isBundle = !!bundlePrice && Number.isFinite(bundlePrice.total) && bundlePrice.total > 0
    const cabinPrice = isBundle ? bundlePrice : pricesByCabinCategory?.[category]
    const memberPrice = isBundle ? undefined : departureMemberPricingByCategory[category]?.memberPrice
    const { primaryPrice, secondaryPrice, promotionalPrice } = getCruiseOfferPriceVariation(offer, cabinPrice)
    const memberPriceVariation = getCruiseOfferMemberPriceVariation(offer, memberPrice)

    return {
      category,
      isBundle,
      primaryPrice,
      secondaryPrice,
      promotionalPrice,
      hasPrice: !!(promotionalPrice?.total || primaryPrice.total || secondaryPrice?.total),
      memberPrice: memberPriceVariation?.primaryPrice.total,
      memberPriceType: isBundle ? undefined : departureMemberPricingByCategory[category]?.memberPriceType,
      discountPercentage: isBundle ? undefined : pricesByCabinCategory?.[category]?.brochureDiscountPercentage,
    }
  })
}
